import React from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import "./index.css";
import "./styles-v2/v2-root.css";
import "./styles-v2/v2-root-dark.css";
import App from "./App.js";
import { ThemeProvider } from "./context-v2/ThemeContext";
import { QueryClient, QueryClientProvider } from "react-query";
import "bootstrap/dist/css/bootstrap.css";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { installToastDedup } from "./lib/toastDedup";
import { useToastMs } from "./components/ui/toastPrefs";
import { installAttribution } from "./lib/attribution";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { persistor, store } from "./redux/store";
import { PersistGate } from "redux-persist/integration/react";
import Loader from "./components/ui/Loader.jsx";
import { installChunkReloadGuard } from "./utils/chunkReload";
import { initTagGate } from "./utils/tagGate";

// Resilience: recover from stale-deploy chunk-load failures + make them observable.
installChunkReloadGuard();

// Collapse identical toasts (e.g. a staff dashboard's repeated feature-gate 403s) into a single toast.
installToastDedup();
// wtfgos.com: the visit's campaign (utm_*), kept only with analytics consent — a demo booking,
// call-back or signup sends it, and the lead it makes carries it (lib/attribution.js)
installAttribution();

// CONSENT GATE (spec §7). GTM is no longer hard-coded into public/index.html —
// it is loaded here, at boot, ONLY if marketing consent has been granted, and
// re-checked whenever the user changes their decision.
//
// This call is the whole point of the gate: tagGate.js was fully built and unit
// tested while nothing called it, which meant GTM never loaded for anyone. That
// fails in the safe direction (no tag fires without consent) but it is silently
// wrong — consenting users got no analytics and every test stayed green.
// src/utils/__tests__/tagGateBootstrap.test.js pins this wiring at the source.
initTagGate();

const queryClient = new QueryClient();

const root = ReactDOM.createRoot(document.getElementById("root"));

const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

if (!isIOS()) {
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker
      .register("/firebase-messaging-sw.js")
      .then(function (registration) {
        console.log(
          "Service Worker registered with scope:",
          registration.scope
        );
      })
      .catch(function (err) {
        console.error("Service Worker registration failed:", err);
      });
  } else {
    console.error("Service Worker is not supported in this browser.");
  }
}

// The app's single toast host. Its timer follows the gym's Web App look
// (Branded App → Web App → Popups); position / style are CSS on <html>.
function AppToasts() {
  const toastMs = useToastMs();
  return <ToastContainer position="top-center" style={{ zIndex: 999999 }} pauseOnFocusLoss={false} limit={3} autoClose={toastMs} />;
}

root.render(
  <React.StrictMode>
    <Provider store={store}>
      <PersistGate loading={<Loader />} persistor={persistor}>
        <QueryClientProvider client={queryClient}>
          {/* QA TC_UI_003: a validation toast left on screen while the owner looked away kept
              its timer paused (pauseOnFocusLoss), so old messages stacked up and read as
              re-sent; at most 3 at once. */}
          <AppToasts />

          <ThemeProvider>
            <BrowserRouter>
              <Routes>
                <Route path="/*" element={<App />} />
              </Routes>
            </BrowserRouter>
          </ThemeProvider>
        </QueryClientProvider>
      </PersistGate>
    </Provider>
  </React.StrictMode>
);
